import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { LinkSquare } from "@/assets/icons";
import { Badge, Card } from "@/atoms";
import { BadgeStateEnum } from "@/enums";
import { AlfredTableDataInterface } from "@/interfaces";
import { generateValidClassNameHandler } from "@/lib/utils";
import { Button } from "@/ui/button";

/**
 * Column priority system for responsive tables
 * Priority 3: Always visible (date, amount)
 * Priority 2: Show on mobile (name, description, default)
 * Priority 1: Hide on mobile (status)
 */
const getColumnPriority = (key: string): number => {
    const lowerKey = key.toLowerCase();
    if (lowerKey.includes("date")) return 3;
    if (lowerKey.includes("amount")) return 3;
    if (lowerKey.includes("name") || lowerKey.includes("description")) return 2;
    if (lowerKey.includes("status")) return 1;
    return 2;
};

const getStatusBadge = (value: string | number) => {
    const strValue = String(value).toUpperCase();
    // Ledger statuses (green/success)
    if (strValue === "POSTED" || strValue === "ACTIVE" || strValue === "COMPLETED" || strValue === "CLEARED") return <Badge state={BadgeStateEnum.SUCCESS}>{value}</Badge>;
    // Matching status: MATCHED (green/success)
    if (strValue === "MATCHED") return <Badge state={BadgeStateEnum.SUCCESS}>{value}</Badge>;

    // Ledger statuses (yellow/warning)
    if (strValue === "PENDING" || strValue === "RUNNING" || strValue === "IN_PROGRESS") return <Badge state={BadgeStateEnum.WARNING}>{value}</Badge>;
    // Matching statuses: REVIEW, PARTIALLY_MATCHED (yellow/warning)
    if (strValue === "REVIEW" || strValue === "PARTIALLY_MATCHED") return <Badge state={BadgeStateEnum.WARNING}>{value}</Badge>;

    // Ledger statuses (red/danger)
    if (strValue === "ERROR" || strValue === "FAILED" || strValue === "INACTIVE") return <Badge state={BadgeStateEnum.DANGER}>{value}</Badge>;
    // Matching statuses: EXCEPTIONS, UNMATCHED (red/danger)
    if (strValue === "EXCEPTIONS" || strValue === "UNMATCHED") return <Badge state={BadgeStateEnum.DANGER}>{value}</Badge>;

    // Matching status: NOT_PROCESSED (gray/light)
    if (strValue === "NOT_PROCESSED") return <Badge state={BadgeStateEnum.LIGHT}>{value}</Badge>;

    return value;
};

const formatCellValue = (value: string | number, key: string) => {
    // Check if it looks like a status field
    if (key.toLowerCase().includes("status")) return getStatusBadge(value);

    // Check if it looks like a currency/amount
    if (typeof value === "number" && key.toLowerCase().includes("amount")) {
        return new Intl.NumberFormat(
            "en-US",
            {
                currency: "USD",
                minimumFractionDigits: 2,
                style: "currency",
            },
        ).format(value);
    }

    return value;
};

export const AlfredCompactTable = ({
    className,
    data,
    initialRowLimit = 5,
} : {
    className?: string,
    data: AlfredTableDataInterface,
    initialRowLimit?: number,
}) => {
    const navigate = useNavigate();
    const [displayedRows, setDisplayedRows] = useState(initialRowLimit);

    // Defensive checks for missing data
    const rows = data?.rows || [];
    const columns = data?.columns || [];
    const totalCount = data?.totalCount ?? rows.length;

    const handleSeeAll = useCallback(
        () => {
            if (data?.seeAllLink) navigate(data.seeAllLink);
        },
        [data?.seeAllLink, navigate],
    );

    const handleLoadMore = useCallback(
        () => {
            setDisplayedRows((prev) => Math.min(
                prev + 5,
                rows.length,
            ));
        },
        [rows.length],
    );

    const visibleRows = rows.slice(
        0,
        displayedRows,
    );
    const hasMore = displayedRows < rows.length;

    // Return early if no data
    if (!data || columns.length === 0) {
        return (
            <Card
                className={generateValidClassNameHandler(
                    "w-full border shadow-sm",
                    className,
                )}
                content={(
                    <div className="py-4 text-center text-sm text-gray-500">
                        No table data available
                    </div>
                )}
            />
        );
    }

    const headerContent = (
        <div className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{data.title || "Data"}</span>
                <Badge state={BadgeStateEnum.LIGHT}>
                    {rows.length}
                    {" "}
                    of
                    {" "}
                    {totalCount}
                </Badge>
            </div>
            {data.seeAllLink && (
                <Button
                    className="!h-auto !p-0 text-xs"
                    size="sm"
                    variant="ghost"
                    onClick={handleSeeAll}
                >
                    See All
                    <LinkSquare className="ml-1 size-3" />
                </Button>
            )}
        </div>
    );

    const cardContent = (
        <>
            <div className="overflow-x-auto rounded border">
                <table className="w-full min-w-[280px] text-xs">
                    <thead>
                        <tr className="border-b bg-gray-50">
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    className={generateValidClassNameHandler(
                                        "whitespace-nowrap px-2 py-1.5 font-medium text-gray-600",
                                        col.align === "right" && "text-right",
                                        col.align === "center" && "text-center",
                                        // Hide low-priority columns on mobile
                                        getColumnPriority(col.key) < 2 && "hidden sm:table-cell",
                                    )}
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {visibleRows.map((row, idx) => (
                            <tr
                                className="border-b last:border-b-0"
                                key={idx} // eslint-disable-line
                            >
                                {columns.map((col) => (
                                    <td
                                        key={col.key}
                                        className={generateValidClassNameHandler(
                                            "px-2 py-1.5",
                                            col.align === "right" && "text-right",
                                            col.align === "center" && "text-center",
                                            // Hide low-priority columns on mobile
                                            getColumnPriority(col.key) < 2 && "hidden sm:table-cell",
                                        )}
                                    >
                                        {formatCellValue(
                                            row[col.key],
                                            col.key,
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Load More button as part of content (Option 3) */}
            {hasMore && (
                <div className="mt-2 flex justify-center border-t pt-2">
                    <Button
                        className="text-xs"
                        size="sm"
                        variant="ghost"
                        onClick={handleLoadMore}
                    >
                        Load More (
                        {rows.length - displayedRows}
                        {" "}
                        remaining)
                    </Button>
                </div>
            )}
        </>
    );

    return (
        <Card
            content={cardContent}
            contentClassName="pb-2 pt-0"
            headerClassName="pb-2"
            otherHeaderData={headerContent}
            className={generateValidClassNameHandler(
                "w-full border shadow-sm",
                className,
            )}
        />
    );
};
